import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Button } from '../ui/button';
import { useNavigate } from 'react-router-dom';
import { ArrowUpCircle, CreditCard, Building, HelpCircle } from 'lucide-react';
import { toast } from '../../hooks/use-toast';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Settings = ({ userId, user }) => {
  const navigate = useNavigate();
  const [upiDetails, setUpiDetails] = useState({
    upiId: '',
    holderName: ''
  });
  const [bankDetails, setBankDetails] = useState({
    accountNumber: '',
    bankName: '',
    ifscCode: '',
    accountHolderName: ''
  });
  const [loading, setLoading] = useState(false);
  const [paymentSettings, setPaymentSettings] = useState(null);

  const fetchSettings = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/settings/${userId}`);
      if (response.data) {
        setUpiDetails(response.data.upiDetails || { upiId: '', holderName: '' });
        setBankDetails(response.data.bankDetails || {
          accountNumber: '',
          bankName: '',
          ifscCode: '',
          accountHolderName: ''
        });
      }
    } catch (error) {
      console.error('Error fetching settings:', error);
    }
  }, [userId]);

  const fetchPaymentSettings = async () => {
    try {
      const response = await axios.get(`${API}/payment-settings`);
      setPaymentSettings(response.data);
    } catch (error) {
      console.error('Error fetching payment settings:', error);
    }
  };

  useEffect(() => {
    if (userId) {
      fetchSettings();
    }
    fetchPaymentSettings();
  }, [userId, fetchSettings]);

  const handleSaveSettings = async () => {
    setLoading(true);
    try {
      await axios.put(`${API}/settings/${userId}`, {
        upiDetails,
        bankDetails,
        paymentMethod: upiDetails.upiId ? 'UPI' : bankDetails.accountNumber ? 'Bank Transfer' : 'Not Set'
      });
      toast({
        title: '✅ Success',
        description: 'Settings saved successfully',
      });
    } catch (error) {
      toast({
        title: '❌ Error',
        description: 'Failed to save settings',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  const handleUpgradePlan = () => {
    // Navigate to payment page with upgrade flag
    const currentPlan = user?.activePlan || 'Basic';
    const discount = paymentSettings?.upgradeDiscount || 1000;
    
    // Get prices from payment settings or use defaults
    const advancedPrice = paymentSettings?.plans?.Advanced?.price || 1999;
    const professionalPrice = paymentSettings?.plans?.Professional?.price || 2999;
    const advancedQR = paymentSettings?.plans?.Basic?.qrCode || 'https://customer-assets.emergentagent.com/job_utr-auth-fix/artifacts/jq6ri19s_%5BRs.%20999%5D%20DropSquadz%20G-Pay.png';
    const professionalQR = paymentSettings?.plans?.Advanced?.qrCode || 'https://customer-assets.emergentagent.com/job_utr-auth-fix/artifacts/u9l74gtn_%5BRs.%201999%5D%20DropSquadz%20G-Pay.png';
    
    let upgradePlan = null;
    if (currentPlan === 'Basic') {
      // Upgrade from Basic to Advanced
      upgradePlan = { 
        name: 'Advanced', 
        price: advancedPrice - discount, 
        originalPrice: advancedPrice,
        qrCode: advancedQR
      };
    } else if (currentPlan === 'Advanced') {
      // Upgrade from Advanced to Professional
      upgradePlan = { 
        name: 'Professional', 
        price: professionalPrice - discount, 
        originalPrice: professionalPrice,
        qrCode: professionalQR
      };
    }

    if (upgradePlan) {
      navigate('/payment', { 
        state: { 
          plan: {
            name: upgradePlan.name,
            price: upgradePlan.price,
            qrCode: upgradePlan.qrCode,
            isUpgrade: true,
            originalPrice: upgradePlan.originalPrice,
            discount: discount
          }
        } 
      });
    }
  };

  return (
    <div className="space-y-6">
      {/* Upgrade Plan Card */}
      {user?.activePlan && user.activePlan !== 'Professional' && (
        <Card className="bg-gradient-to-br from-blue-500/10 to-blue-500/10 border-slate-200 hover:border-purple-500/50 transition-all">
          <CardContent className="pt-6">
            <div className="flex items-start justify-between">
              <div className="flex items-start space-x-4">
                <div className="p-3 bg-gradient-to-br from-blue-500 to-blue-500 rounded-lg">
                  <ArrowUpCircle className="w-6 h-6 text-slate-800" />
                </div>
                <div>
                  <h3 className="text-xl font-bold bg-gradient-to-r from-blue-400 to-cyan-400 bg-clip-text text-transparent mb-2">
                    Upgrade Your Plan
                  </h3>
                  <p className="text-slate-600 mb-4">
                    Get ₹1000 OFF on plan upgrade! Unlock more features and benefits.
                  </p>
                  <Button 
                    onClick={handleUpgradePlan}
                    className="bg-gradient-to-r from-blue-500 to-blue-500 hover:from-blue-600 hover:to-pink-600 shadow-lg shadow-blue-500/50 hover:shadow-blue-500/75 transition-all"
                  >
                    Upgrade Now
                  </Button>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* UPI Details */}
      <Card className="bg-white shadow-lg border-slate-200">
        <CardHeader>
          <div className="flex items-center space-x-3">
            <div className="p-2 bg-gradient-to-br from-blue-500 to-blue-500 rounded-lg">
              <CreditCard className="w-5 h-5 text-slate-800" />
            </div>
            <CardTitle className="bg-gradient-to-r from-blue-400 to-cyan-400 bg-clip-text text-transparent">UPI Details</CardTitle>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          <div>
            <Label className="text-slate-600">UPI ID</Label>
            <Input
              value={upiDetails.upiId}
              onChange={(e) => setUpiDetails({ ...upiDetails, upiId: e.target.value })}
              placeholder="example@upi"
              className="bg-white border-slate-200 focus:border-blue-500 text-slate-800 mt-2"
            />
          </div>
          <div>
            <Label className="text-slate-600">Holder Name</Label>
            <Input
              value={upiDetails.holderName}
              onChange={(e) => setUpiDetails({ ...upiDetails, holderName: e.target.value })}
              placeholder="Your name"
              className="bg-white border-slate-200 focus:border-blue-500 text-slate-800 mt-2"
            />
          </div>
        </CardContent>
      </Card>

      {/* Bank Details */}
      <Card className="bg-white shadow-lg border-slate-200">
        <CardHeader>
          <div className="flex items-center space-x-3">
            <div className="p-2 bg-gradient-to-br from-blue-500 to-blue-500 rounded-lg">
              <Building className="w-5 h-5 text-slate-800" />
            </div>
            <CardTitle className="bg-gradient-to-r from-blue-400 to-cyan-400 bg-clip-text text-transparent">Bank Details</CardTitle>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          <div>
            <Label className="text-slate-600">Account Number</Label>
            <Input
              value={bankDetails.accountNumber}
              onChange={(e) => setBankDetails({ ...bankDetails, accountNumber: e.target.value })}
              placeholder="1234567890"
              className="bg-white border-slate-200 focus:border-blue-500 text-slate-800 mt-2"
            />
          </div>
          <div>
            <Label className="text-slate-600">Bank Name</Label>
            <Input
              value={bankDetails.bankName}
              onChange={(e) => setBankDetails({ ...bankDetails, bankName: e.target.value })}
              placeholder="Bank Name"
              className="bg-white border-slate-200 focus:border-blue-500 text-slate-800 mt-2"
            />
          </div>
          <div>
            <Label className="text-slate-600">IFSC Code</Label>
            <Input
              value={bankDetails.ifscCode}
              onChange={(e) => setBankDetails({ ...bankDetails, ifscCode: e.target.value })}
              placeholder="ABCD0123456"
              className="bg-white border-slate-200 focus:border-blue-500 text-slate-800 mt-2"
            />
          </div>
          <div>
            <Label className="text-slate-600">Account Holder Name</Label>
            <Input
              value={bankDetails.accountHolderName}
              onChange={(e) => setBankDetails({ ...bankDetails, accountHolderName: e.target.value })}
              placeholder="Your name"
              className="bg-white border-slate-200 focus:border-blue-500 text-slate-800 mt-2"
            />
          </div>
        </CardContent>
      </Card>

      {/* Support Option */}
      <Card className="bg-white shadow-lg border-slate-200">
        <CardHeader>
          <div className="flex items-center space-x-3">
            <div className="p-2 bg-gradient-to-br from-green-500 to-emerald-500 rounded-lg">
              <HelpCircle className="w-5 h-5 text-slate-800" />
            </div>
            <CardTitle className="text-blue-600">Support</CardTitle>
          </div>
        </CardHeader>
        <CardContent>
          <p className="text-slate-600 mb-4">
            Need help? Contact our support team for assistance.
          </p>
          <div className="flex flex-col sm:flex-row gap-3">
            <Button 
              onClick={() => window.open('https://api.whatsapp.com/send/?phone=918965883955&text=Need Support!', '_blank')}
              className="bg-gradient-to-r from-green-500 to-emerald-500 hover:from-green-600 hover:to-emerald-600 flex-1"
            >
              WhatsApp Support
            </Button>
            <Button 
              variant="outline"
              onClick={() => window.location.href = 'mailto:support@dropsquadz.tech'}
              className="border-green-500 text-green-600 hover:bg-green-500/10 flex-1"
            >
              Email Support
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Save Button */}
      <Button 
        onClick={handleSaveSettings}
        disabled={loading}
        className="w-full bg-gradient-to-r from-blue-500 via-purple-500 to-blue-500 hover:from-blue-600 hover:via-purple-600 hover:to-pink-600 shadow-lg hover:shadow-xl transition-all py-6 text-lg"
      >
        {loading ? 'Saving...' : 'Save Settings'}
      </Button>
    </div>
  );
};

export default Settings;